import { Fragment, useEffect, useRef, useState } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
// Note: Enterprise-only pricing model - no PricingCard component needed
import {
  CubeTransparentIcon,
  TableCellsIcon,
  ShieldCheckIcon,
  DocumentChartBarIcon,
  CpuChipIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  MapPinIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  ScaleIcon,
  CheckIcon,
  ArrowLeftIcon,
  PlayIcon,
  SparklesIcon,
  ClockIcon,
  KeyIcon,
  DocumentCheckIcon,
  WrenchScrewdriverIcon,
  InboxIcon,
  CalculatorIcon,
  ChartBarSquareIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  BellAlertIcon,
  LockClosedIcon,
  ClipboardDocumentCheckIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import { apiClient } from '@/api/client';
import { FadeIn, StaggeredFadeIn } from '../components/animations/FadeIn';
import { getProductBySlug } from '../types/products';
import { PolygonIcon } from '../components/icons/PolygonIcon';
import { PhreaticIcon } from '../components/icons/PhreaticIcon';
import { PulseWaveIcon } from '../components/icons/PulseWaveIcon';
import { PathfinderIcon } from '../components/icons/PathfinderIcon';
import { Seo } from '../components/Seo';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  CubeTransparentIcon,
  TableCellsIcon,
  ShieldCheckIcon,
  DocumentChartBarIcon,
  CpuChipIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  MapPinIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowPathIcon,
  CalendarDaysIcon,
  ScaleIcon,
  PolygonIcon,
  PhreaticIcon,
  PulseWaveIcon,
  PathfinderIcon,
  SparklesIcon,
  ClockIcon,
  DocumentCheckIcon,
  WrenchScrewdriverIcon,
  InboxIcon,
  CalculatorIcon,
  ChartBarSquareIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  BellAlertIcon,
  LockClosedIcon,
  ClipboardDocumentCheckIcon,
  BeakerIcon,
};

const accentColors: Record<string, { bg: string; border: string; text: string; gradient: string; button: string }> = {
  cyan: {
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/30',
    text: 'text-cyan-400',
    gradient: 'from-cyan-500/20 to-cyan-600/5',
    button: 'bg-cyan-600 hover:bg-cyan-500',
  },
  emerald: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-400',
    gradient: 'from-emerald-500/20 to-emerald-600/5',
    button: 'bg-emerald-600 hover:bg-emerald-500',
  },
  violet: {
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/30',
    text: 'text-violet-400',
    gradient: 'from-violet-500/20 to-violet-600/5',
    button: 'bg-violet-600 hover:bg-violet-500',
  },
};

// ── Phreatic product-page showcase ───────────────────────────────────
// The Phreatic capabilities section renders as a vertical stack of large
// cyan-glow containers — one capability at a time — each pairing the
// description with its screenshot(s). Multi-image capabilities cycle a
// slideshow. Text-only capabilities (no screenshot yet) render compact.

interface ShowcaseSlide {
  src: string;
  label: string;
}

interface ShowcaseItem {
  title: string;
  description: string;
  icon: string;
  section: 'core' | 'well-ops';
  slides: ShowcaseSlide[]; // empty array = text-only container
}

const PHREATIC_SHOWCASE: ShowcaseItem[] = [
  {
    title: 'Command',
    section: 'core',
    description:
      'Start anywhere in Texas. Search a location, draw your reference ' +
      'region, then outline the property boundary — Phreatic snips every ' +
      'well inside and runs a full assessment, satellite imagery to ' +
      'certified report.',
    icon: 'MagnifyingGlassIcon',
    slides: [
      { src: '/phreatic/command.jpg', label: '1 · Command' },
      { src: '/phreatic/reference.jpg', label: '2 · Reference Region' },
      { src: '/phreatic/property.jpg', label: '3 · Property Boundary' },
    ],
  },
  {
    title: 'Per-Aquifer Engine',
    section: 'core',
    description:
      'Distinct prediction models for each major Texas aquifer — Edwards, ' +
      'Ogallala, Gulf Coast, Dockum, and a general fallback. A spatial ' +
      'classifier picks the right model per well — no cross-aquifer ' +
      'averaging, no one-size-fits-all forecasting. Forecasts run on a ' +
      'stacked ML ensemble across multiple horizons from near-term ' +
      'through 20 years.',
    icon: 'CpuChipIcon',
    slides: [
      { src: '/phreatic/aquifer-toggle-ogallala.jpg', label: 'Ogallala Bundle' },
      { src: '/phreatic/aquifer-toggle-dockum.jpg', label: 'Dockum Bundle' },
    ],
  },
  {
    title: 'Hydrograph',
    section: 'core',
    description:
      'Professional-grade multi-well water-level chart. Model prediction ' +
      'overlay with confidence intervals, per-well visibility toggles, ' +
      'aquifer filtering, crosshair tooltip, scroll-zoom on either axis.',
    icon: 'ChartBarIcon',
    slides: [{ src: '/phreatic/hydrograph.jpg', label: 'Hydrograph' }],
  },
  {
    title: '3D Water Table',
    section: 'core',
    description:
      'Interactive 3D surface of the water table with well markers placed ' +
      'on the surface. Vertical exaggeration, hover labels, contour ' +
      'overlays. The gradient slideshow steps the predicted surface from ' +
      '3 months out to 20 years.',
    icon: 'CubeTransparentIcon',
    slides: [
      { src: '/phreatic/gradient-3mo.jpg', label: '3 Months' },
      { src: '/phreatic/gradient-1yr.jpg', label: '1 Year' },
      { src: '/phreatic/gradient-2yr.jpg', label: '2 Years' },
      { src: '/phreatic/gradient-5yr.jpg', label: '5 Years' },
      { src: '/phreatic/gradient-10yr.jpg', label: '10 Years' },
      { src: '/phreatic/gradient-20yr.jpg', label: '20 Years' },
    ],
  },
  {
    title: 'Well Map',
    section: 'core',
    description:
      '2D satellite map with well markers and smooth water-table contours ' +
      'laid over high-resolution satellite imagery. Color-coded by status; ' +
      'flow direction read straight off the contour gradient.',
    icon: 'MapPinIcon',
    slides: [{ src: '/phreatic/well-map.jpg', label: 'Well Map' }],
  },
  {
    title: 'Cross-Section',
    section: 'core',
    description:
      'Vertical geological profile along any user-drawn line — ground ' +
      'surface, water table, and aquifer base over the full USGS / ' +
      'Macrostrat formation stack, with per-well DTW callouts.',
    icon: 'ChartBarSquareIcon',
    slides: [{ src: '/phreatic/cross-section.jpg', label: 'Geological Cross-Section' }],
  },
  {
    title: 'Volume',
    section: 'core',
    description:
      'Available groundwater volume using industry-standard hydrogeology ' +
      '(Fetter). Recoverable vs. Floyd-zone split, projected volume across ' +
      'multiple horizons, demand-adjusted depletion timeline, plus TDS / ' +
      'nitrate / arsenic / fluoride checked against MCLs for livestock, ' +
      'irrigation, and drinking suitability.',
    icon: 'ScaleIcon',
    slides: [{ src: '/phreatic/volume.jpg', label: 'Volume Available' }],
  },
  {
    title: 'Economics',
    section: 'core',
    description:
      'Revenue per acre-foot across every use case — data center, cattle, ' +
      'municipal, dairy, row crops. Demand modeling, water-rights ' +
      'valuation, and crop trade-off analysis turn aquifer volume into a ' +
      'dollar figure for the loan memo.',
    icon: 'CurrencyDollarIcon',
    slides: [{ src: '/phreatic/economics.jpg', label: 'Revenue by Use Case' }],
  },
  {
    title: 'Lending Risk & TCEQ 230 Report',
    section: 'core',
    description:
      'Structured groundwater availability report — Approve / Approve ' +
      'with Conditions / Additional Review / Decline. TCEQ Chapter 230 ' +
      'categorization, GCD jurisdiction, drought resilience, print-ready ' +
      'PDF.',
    icon: 'DocumentCheckIcon',
    slides: [{ src: '/phreatic/reporting.jpg', label: 'Reporting Library' }],
  },
  {
    title: 'Well Inventory',
    section: 'well-ops',
    description:
      'All site well assets in one table — imported wells + model-recommended ' +
      'placements. Excel / CSV import, manual gauging entry, and ' +
      'continuous re-prediction on every new reading.',
    icon: 'TableCellsIcon',
    slides: [{ src: '/phreatic/well-inventory.jpg', label: 'Well Inventory' }],
  },
  {
    title: 'Well Intelligence',
    section: 'well-ops',
    description:
      'Placement engine — recommends WHERE to drill and HOW DEEP. ' +
      'Aquifer-strict (every candidate inside the right hydrologic unit) ' +
      'and parcel-bounded (inside your property boundary) with ' +
      'physics-based interference scoring.',
    icon: 'SparklesIcon',
    slides: [{ src: '/phreatic/well-intelligence.jpg', label: 'Well Intelligence' }],
  },
  {
    title: 'Cavitation Risk Timeline',
    section: 'well-ops',
    description:
      'Per-well cavitation risk from 3 months to 20 years out — an early ' +
      'warning system flagging which wells will lose pump submergence ' +
      'first under the current drawdown trajectory.',
    icon: 'ExclamationTriangleIcon',
    slides: [{ src: '/phreatic/cavitation.jpg', label: 'Cavitation Early Warning' }],
  },
  {
    title: 'Sustainable Yield & Pump Control',
    section: 'well-ops',
    description:
      'Maximum sustainable pump rate that holds saturated thickness above ' +
      'threshold for a target lifespan. Interference-aware pump scheduling ' +
      'generates optimal on/off cycles across all wells and tracks ' +
      'conflicts.',
    icon: 'ArrowPathIcon',
    slides: [{ src: '/phreatic/sustainable-yield.jpg', label: 'Sustainable Pump Rate Scenarios' }],
  },
  {
    title: 'Cone of Depression',
    section: 'well-ops',
    description:
      'Physics-based drawdown cross-sections for single wells and full ' +
      'wellfields. The ROI map shows the interference radius around each ' +
      'pumping well — driven by use-case pump rate.',
    icon: 'ArrowTrendingDownIcon',
    slides: [
      { src: '/phreatic/cone-of-depression.jpg', label: 'Single Well' },
      { src: '/phreatic/wellfield-cod.jpg', label: 'Wellfield Cross-Section' },
      { src: '/phreatic/cod-roi-interference.jpg', label: 'ROI & Interference' },
    ],
  },
];

// Polygon capability showcase — same vertical cyan-glow treatment as
// Phreatic. All items sit in the 'core' section (no sub-divider).
const POLYGON_SHOWCASE: ShowcaseItem[] = [
  {
    title: 'Portfolio',
    section: 'core',
    description:
      'Every site you manage, ranked by 20-year liability. Priority ' +
      'scores, worsening-trend flags, wells-above-MCL counts, and a ' +
      'national map of the sites that need attention first — capital ' +
      'allocation across the whole book at a glance.',
    icon: 'ChartBarSquareIcon',
    slides: [
      { src: '/polygon/portfolio-map.jpg', label: 'Attention Map' },
      { src: '/polygon/portfolio-analytics.jpg', label: 'Priority Ranking' },
    ],
  },
  {
    title: 'Site Dashboard',
    section: 'core',
    description:
      'One site, fully assessed. PSH mass, dissolved peak, remediation ' +
      'status, Start-Now vs. 20-year liability, delineation cost, and a ' +
      'contaminants-vs-MCL exceedance chart — all on a single ' +
      'time-sliderable screen.',
    icon: 'DocumentChartBarIcon',
    slides: [{ src: '/polygon/dashboard.jpg', label: 'Site Dashboard' }],
  },
  {
    title: '2D Plume Map',
    section: 'core',
    description:
      'Three-plume simulation (PSH + dissolved benzene + dissolved TPH) ' +
      'on satellite tiles. Smooth contour rendering with future-horizon ' +
      'plume polygons drawn against the active regulatory MCL. ' +
      'Ctrl-scroll zoom.',
    icon: 'MapPinIcon',
    slides: [{ src: '/polygon/2d-plume.jpg', label: '2D Plume Map' }],
  },
  {
    title: '3D Plume Scene',
    section: 'core',
    description:
      'Volumetric 3D rendering of all three plumes with vertical ' +
      'exaggeration. Well markers, plume sources, and contour bands sit ' +
      'on the same scene as 2D — toggle between views without losing ' +
      'time-slider state.',
    icon: 'CubeTransparentIcon',
    slides: [{ src: '/polygon/3d-plume.jpg', label: '3D Plume Scene' }],
  },
  {
    title: 'Multi-Well Multi-Analyte Chart',
    section: 'core',
    description:
      'Professional-grade hydrograph showing every well, every analyte, ' +
      'and every prediction horizon as connected points on one chart — ' +
      'pan, zoom, crosshair tooltip, per-series visibility.',
    icon: 'ChartBarIcon',
    slides: [{ src: '/polygon/analyte-graph.jpg', label: 'Multi-Analyte Chart' }],
  },
  {
    title: 'Well Predictions',
    section: 'core',
    description:
      'Per-well per-analyte forecasts at multiple horizons from current ' +
      'through 20 years out, in a sortable table. Each cell flags ' +
      'exceedance against the active regulatory library.',
    icon: 'TableCellsIcon',
    slides: [{ src: '/polygon/well-predictions.jpg', label: 'Well Predictions' }],
  },
  {
    title: 'Delineation Intelligence',
    section: 'core',
    description:
      'Priority-ranked monitoring well recommendations driven by ' +
      'future-horizon exceedance, distance from source, and trigger-well ' +
      'combined rank. Inline DW rename, plan-and-place, live cost ' +
      'integration.',
    icon: 'MagnifyingGlassIcon',
    slides: [{ src: '/polygon/delineation-intel.jpg', label: 'Delineation Intelligence' }],
  },
  {
    title: 'Cost Analysis',
    section: 'core',
    description:
      'Multi-horizon liability curve — Start Now, 20-Year Liability, ' +
      'Delineation costs. Forecasts are reliability-capped per analyte so ' +
      'the cost engine only commits where the model is confident. Cost ' +
      'escalation timeline, sentinel-well count, and active remediation ' +
      'system tracking.',
    icon: 'CurrencyDollarIcon',
    slides: [{ src: '/polygon/cost-analysis.jpg', label: 'Cost Analysis' }],
  },
];

// Slideshow used inside each product capability container. Single-slide
// items render a static image; multi-slide items auto-advance (paused on
// hover) with a label chip + dot indicators.
function ShowcaseSlideshow({ slides }: { slides: ShowcaseSlide[] }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const multi = slides.length > 1;

  useEffect(() => {
    if (!multi || paused) return;
    const t = setInterval(() => setIdx(i => (i + 1) % slides.length), 3500);
    return () => clearInterval(t);
  }, [multi, paused, slides.length]);

  return (
    <div
      className="relative h-full w-full min-h-[260px] lg:min-h-[380px] bg-mono-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((s, i) => (
        <img
          key={s.src}
          src={s.src}
          alt={s.label}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-contain p-3 transition-opacity duration-500 ${
            i === idx ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        />
      ))}
      {/* Label chip */}
      <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-mono-950/85 backdrop-blur border border-cyan-400/30">
        <span className="text-[10px] text-cyan-300 tracking-[0.2em] font-medium">
          {slides[idx]?.label}
        </span>
      </div>
      {/* Dot indicators */}
      {multi && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Slide ${i + 1}`}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? 'w-6 bg-cyan-400' : 'w-1.5 bg-mono-600 hover:bg-mono-500'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Employee access component for PRECEPT - "have a code?" section
function EmployeeAccess({ colors }: { colors: typeof accentColors.cyan }) {
  const [showForm, setShowForm] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const getUsername = () => {
    if (!firstName || !lastName) return '';
    return (firstName.charAt(0) + lastName).toLowerCase().replace(/[^a-z0-9]/g, '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (accessCode.toLowerCase() !== 'talon') {
      setError('INVALID ACCESS CODE');
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      setError('NAME REQUIRED');
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post('/auth/employee/signup', {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        access_code: 'argos',
      });

      const data = response.data;
      navigate('/precept/login', {
        replace: true,
        state: {
          username: data.user.username,
          signupSuccess: true,
          fullName: data.user.name
        }
      });
    } catch (err: any) {
      const message = err.response?.data?.detail || 'SIGNUP FAILED';
      setError(typeof message === 'string' ? message.toUpperCase() : 'SIGNUP FAILED');
    } finally {
      setLoading(false);
    }
  };

  const previewUsername = getUsername();

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className="flex items-center gap-3 px-6 py-4 border border-mono-700 hover:border-mono-500 text-mono-400 hover:text-mono-200 transition-colors text-sm tracking-widest rounded-lg"
      >
        <KeyIcon className="h-5 w-5" />
        HAVE A CODE?
      </button>
    );
  }

  return (
    <div className={`border ${colors.border} rounded-lg p-6 bg-gradient-to-br ${colors.gradient} max-w-md`}>
      <p className="text-[10px] text-mono-500 tracking-[0.3em] mb-4">EMPLOYEE ACCESS</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <p className="text-xs text-red-400 tracking-wider">{error}</p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            className="px-3 py-2 bg-black/50 border border-mono-700 text-mono-100 text-sm focus:border-mono-500 focus:outline-none"
          />
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            className="px-3 py-2 bg-black/50 border border-mono-700 text-mono-100 text-sm focus:border-mono-500 focus:outline-none"
          />
        </div>

        <input
          type="password"
          value={accessCode}
          onChange={(e) => setAccessCode(e.target.value)}
          placeholder="Access code"
          className="w-full px-3 py-2 bg-black/50 border border-mono-700 text-mono-100 text-sm focus:border-mono-500 focus:outline-none"
        />

        {previewUsername && (
          <div className="text-[10px] text-mono-500">
            Username: <span className="text-mono-300 font-mono">{previewUsername}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !firstName || !lastName || !accessCode}
          className={`w-full py-2 text-xs tracking-widest transition-colors ${colors.button} text-white disabled:opacity-50`}
        >
          {loading ? 'CREATING...' : 'ACCESS PRECEPT'}
        </button>
      </form>
    </div>
  );
}

const DATAVAULT_GALLERY = [
  '/data-vault/dv-01.png',
  '/data-vault/dv-02.png',
  '/data-vault/dv-03.png',
  '/data-vault/dv-04.png',
  '/data-vault/dv-05.png',
  '/data-vault/dv-06.png',
  '/data-vault/dv-07.png',
];

const DV_CYLINDER_RADIUS = 480;
// Pixels of wheel delta that complete the carousel (~1.5 viewport heights).
const DV_HIJACK_DISTANCE = 1500;

function applyDvRotation(refs: Array<HTMLDivElement | null>, progress: number) {
  const rotRad = progress * Math.PI * 4; // two full revolutions
  const n = refs.length;
  for (let i = 0; i < n; i++) {
    const el = refs[i];
    if (!el) continue;
    const cardAngle = rotRad + (i / n) * Math.PI * 2;
    const x = Math.sin(cardAngle) * DV_CYLINDER_RADIUS;
    const z = Math.cos(cardAngle); // -1 (back) → +1 (front)
    const scale = Math.max(0.3, 0.55 + z * 0.45);
    el.style.transform = `translate(-50%, -50%) translateX(${x}px) scale(${scale})`;
    el.style.opacity = String(Math.max(0, z * 0.7 + 0.3));
    el.style.zIndex = String(Math.round(z * 10) + 10);
  }
}

export function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const product = slug ? getProductBySlug(slug) : undefined;
  const dvSectionRef = useRef<HTMLElement>(null);
  const dvCardRefs = useRef<Array<HTMLDivElement | null>>([]);

  // Data Vault cylinder carousel. Cards orbit an invisible horizontal cylinder
  // (X = sin(angle) * radius; depth faked via scale + opacity from cos(angle)).
  //
  // Two scroll behaviors:
  //   1. Default — section scrolls past naturally; rotation is bound to the
  //      section's position relative to the viewport, so the user catches
  //      a partial spin in passing.
  //   2. Hovered — wheel input is hijacked: page stops scrolling, each wheel
  //      tick advances the carousel rotation. After the carousel completes
  //      (or rewinds to 0), the wheel passes through and page scrolling
  //      resumes. Mouse-leave also releases the hijack.
  //
  // Imperative DOM updates via refs — bypasses React render so it stays at
  // 60 fps even with rapid scroll input.
  const dvHijackedRef = useRef(false);
  const dvHijackProgressRef = useRef(0);

  // Default scroll-driven rotation
  useEffect(() => {
    if (product?.slug !== 'datavault') return;

    let rafId = 0;
    let ticking = false;

    const update = () => {
      ticking = false;
      if (dvHijackedRef.current) return; // hijack owns rotation while active
      const node = dvSectionRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const winH = window.innerHeight;
      const total = rect.height + winH;
      const passed = winH - rect.top;
      const progress = Math.max(0, Math.min(1, passed / total));
      dvHijackProgressRef.current = progress;
      applyDvRotation(dvCardRefs.current, progress);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      rafId = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(rafId);
    };
  }, [product?.slug]);

  // Hover-gated scroll hijack
  useEffect(() => {
    if (product?.slug !== 'datavault') return;

    const onWheel = (e: WheelEvent) => {
      if (!dvHijackedRef.current) return;
      const dir = e.deltaY > 0 ? 1 : -1;
      const p = dvHijackProgressRef.current;
      // Release at the boundaries in the direction the user is scrolling so
      // they can exit the carousel naturally.
      if (dir > 0 && p >= 1) {
        dvHijackedRef.current = false;
        return;
      }
      if (dir < 0 && p <= 0) {
        dvHijackedRef.current = false;
        return;
      }
      e.preventDefault();
      const next = Math.max(0, Math.min(1, p + e.deltaY / DV_HIJACK_DISTANCE));
      dvHijackProgressRef.current = next;
      applyDvRotation(dvCardRefs.current, next);
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [product?.slug]);

  const onDvMouseEnter = () => {
    dvHijackedRef.current = true;
  };
  const onDvMouseLeave = () => {
    dvHijackedRef.current = false;
  };

  if (!product) {
    return <Navigate to="/" replace />;
  }

  const IconComponent = iconMap[product.icon] || CubeTransparentIcon;
  const colors = accentColors[product.accentColor] || accentColors.cyan;
  // Products with a bespoke vertical capability showcase (screenshots +
  // descriptions) instead of the default icon grid.
  const showcase =
    product.slug === 'phreatic'
      ? PHREATIC_SHOWCASE
      : product.slug === 'polygon'
        ? POLYGON_SHOWCASE
        : null;

  // Title-cased product name for human-facing copy (data stores it uppercase).
  const displayName = product.name.replace(/\S+/g, (w) => w[0] + w.slice(1).toLowerCase());
  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: displayName,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web-based',
    url: `https://argostellus.com/products/${product.slug}`,
    description: product.description,
    featureList: product.features.map((f) => f.title),
    provider: {
      '@type': 'Organization',
      name: 'Argos Tellus',
      url: 'https://argostellus.com',
    },
  };

  return (
    <div className="min-h-screen bg-mono-950">
      <Seo
        title={`${displayName} — ${product.tagline} | Argos Tellus`}
        description={product.description}
        path={`/products/${product.slug}`}
        jsonLd={productJsonLd}
      />
      {/* Header */}
      <header className="border-b border-mono-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <FadeIn direction="left" delay={0} duration={500}>
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-mono-500 hover:text-mono-300 transition-colors">
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="text-xs tracking-widest font-orbitron">HOME</span>
              </Link>
              <span className="text-mono-700 font-orbitron">/</span>
              <span className="text-xl font-bold text-mono-100 tracking-[0.3em] font-orbitron">{product.name}</span>
            </div>
          </FadeIn>
          <FadeIn direction="right" delay={100} duration={500}>
            <div className="flex items-center gap-4">
              <Link
                to="/login"
                className="px-4 py-2 border border-mono-600 text-mono-300 text-xs tracking-widest hover:border-mono-400 transition-colors"
              >
                LOGIN
              </Link>
              <Link
                to="/admin"
                className="px-4 py-2 border border-cyan-500/50 text-cyan-400 text-xs tracking-widest hover:border-cyan-400 hover:bg-cyan-500/10 transition-colors"
              >
                ADMIN
              </Link>
            </div>
          </FadeIn>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-mono-800">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <FadeIn delay={100} duration={600}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`p-4 ${colors.bg} rounded-lg border ${colors.border}`}>
                    <IconComponent className={`h-10 w-10 ${colors.text}`} />
                  </div>
                  <div>
                    <p className={`text-xs ${colors.text} tracking-[0.3em]`}>{product.tagline.toUpperCase()}</p>
                  </div>
                </div>
              </FadeIn>
              <FadeIn delay={200} duration={600}>
                <h1 className="text-4xl lg:text-5xl font-bold text-mono-100 tracking-wide leading-tight">
                  {product.name}
                </h1>
              </FadeIn>
              <FadeIn delay={300} duration={600}>
                <p className="mt-6 text-mono-300 text-base leading-relaxed">
                  {product.description}
                </p>
              </FadeIn>
              {product.demoUrl && (
                <FadeIn delay={400} duration={600}>
                  <div className="mt-8">
                    <a
                      href={product.demoUrl}
                      className="flex items-center gap-2 px-6 py-3 border border-mono-600 text-mono-300 text-xs tracking-widest hover:border-mono-400 transition-colors inline-flex"
                    >
                      <PlayIcon className="h-4 w-4" />
                      WATCH DEMO
                    </a>
                  </div>
                </FadeIn>
              )}
            </div>

            {/* Technical Specs Card */}
            <FadeIn delay={300} duration={600}>
              <div className={`border ${colors.border} rounded-lg p-8 bg-gradient-to-br ${colors.gradient}`}>
                <p className="text-[10px] text-mono-500 tracking-[0.3em] mb-6">TECHNICAL SPECIFICATIONS</p>
                {/* Two-column grid: the label column auto-sizes to the widest
                    label so every value starts at the same x (flush column).
                    Full-width row dividers; the last row drops its border. */}
                <dl className="grid grid-cols-[max-content_1fr] gap-x-8 [&>*]:border-b [&>*]:border-mono-800 [&>*:nth-last-child(-n+2)]:border-0">
                  {product.technicalSpecs.map((spec) => (
                    <Fragment key={spec.label}>
                      <dt className="py-3 text-sm text-mono-400 whitespace-nowrap">{spec.label}</dt>
                      <dd className="py-3 text-sm text-mono-100 font-medium">{spec.value}</dd>
                    </Fragment>
                  ))}
                </dl>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Data Vault scroll-driven cylinder carousel — flat 2D projection of
          a horizontal cylinder. Each card orbits around the center via
          sin(angle); scale + opacity simulate depth. No sticky pinning so
          the section ends cleanly the moment the carousel scrolls past —
          capabilities follows without a trailing gap. */}
      {product.slug === 'datavault' && (
        <section
          ref={dvSectionRef}
          onMouseEnter={onDvMouseEnter}
          onMouseLeave={onDvMouseLeave}
          className="relative h-screen overflow-hidden border-b border-mono-800"
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <FadeIn delay={0} duration={500}>
              <div className="text-center mb-6">
                <p className={`text-[10px] ${colors.text} tracking-[0.4em] mb-3`}>PRODUCT TOUR</p>
                <h2 className="text-2xl font-bold text-mono-100 tracking-wide">
                  SEE DATA VAULT IN ACTION
                </h2>
              </div>
            </FadeIn>

            {/* Cylinder stage. Each card is absolutely centered; the scroll
                effect imperatively writes translateX + scale + opacity. */}
            <div className="relative w-full flex-1">
              {DATAVAULT_GALLERY.map((src, i) => (
                <div
                  key={src}
                  ref={(el) => {
                    dvCardRefs.current[i] = el;
                  }}
                  className="absolute left-1/2 top-1/2 will-change-transform"
                  style={{
                    width: 'clamp(280px, 36vw, 640px)',
                    aspectRatio: '16 / 9',
                  }}
                >
                  <img
                    src={src}
                    alt={`Data Vault screenshot ${i + 1}`}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    className="w-full h-full object-contain rounded-md drop-shadow-[0_30px_60px_rgba(0,0,0,0.55)]"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="border-b border-mono-800">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <FadeIn delay={0} duration={500}>
            <p className="text-[10px] text-mono-500 tracking-[0.4em] mb-4">CAPABILITIES</p>
          </FadeIn>
          <FadeIn delay={100} duration={500}>
            <h2 className="text-2xl font-bold text-mono-100 tracking-wide mb-12">
              WHAT {product.name} CAN DO
            </h2>
          </FadeIn>
          {showcase ? (
            /* Vertical stack of large cyan-glow capability containers, each
               pairing the description with screenshots. */
            <div className="space-y-8">
              {showcase.map((item, idx) => {
                const ItemIcon = iconMap[item.icon] || CheckIcon;
                const hasMedia = item.slides.length > 0;
                // Emit the "Well Operations" divider at the section boundary.
                const showWellOpsHeader =
                  item.section === 'well-ops' &&
                  (idx === 0 || showcase[idx - 1].section !== 'well-ops');

                const textCol = (
                  <div className="p-8 lg:p-10 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2.5 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                        <ItemIcon className="h-5 w-5 text-cyan-400" />
                      </div>
                      <h3 className="text-sm font-semibold text-mono-100 tracking-[0.2em] uppercase">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-sm text-mono-400 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                );

                return (
                  <Fragment key={item.title}>
                    {showWellOpsHeader && (
                      <FadeIn delay={0} duration={500}>
                        <div className="pt-10 pb-2">
                          <h2 className="text-2xl font-bold text-cyan-400 tracking-[0.2em] font-orbitron mb-3">
                            WELL OPERATIONS
                          </h2>
                          <div className="h-px bg-gradient-to-r from-cyan-400/50 via-cyan-400/15 to-transparent" />
                        </div>
                      </FadeIn>
                    )}
                    <FadeIn delay={0} duration={500}>
                      <div className="rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-mono-900 to-mono-950 shadow-[0_0_60px_-20px_rgba(34,211,238,0.4)] overflow-hidden">
                        {hasMedia ? (
                          <div className="grid lg:grid-cols-2">
                            {textCol}
                            <div>
                              <ShowcaseSlideshow slides={item.slides} />
                            </div>
                          </div>
                        ) : (
                          /* Text-only capability — no screenshot yet */
                          textCol
                        )}
                      </div>
                    </FadeIn>
                  </Fragment>
                );
              })}
            </div>
          ) : (
            <StaggeredFadeIn
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              staggerDelay={80}
            >
              {product.features.map((feature) => {
                const FeatureIcon = feature.icon ? iconMap[feature.icon] : CheckIcon;
                return (
                  <div
                    key={feature.title}
                    className={
                      product.slug === 'datavault'
                        ? 'rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-mono-900 to-mono-950 p-6 h-full flex flex-col shadow-[0_0_50px_-22px_rgba(34,211,238,0.45)] hover:border-cyan-400/40 hover:shadow-[0_0_55px_-16px_rgba(34,211,238,0.6)] transition-all duration-300'
                        : 'border border-mono-800 p-6 hover:border-mono-700 transition-colors duration-300 h-full flex flex-col'
                    }
                  >
                    {FeatureIcon && <FeatureIcon className={`h-6 w-6 ${colors.text} mb-4`} />}
                    <h3 className="text-xs font-medium text-mono-200 tracking-widest mb-3">
                      {feature.title.toUpperCase()}
                    </h3>
                    <p className="text-xs text-mono-500 leading-relaxed flex-grow">{feature.description}</p>
                  </div>
                );
              })}
            </StaggeredFadeIn>
          )}
        </div>
      </section>

      {/* Use Cases */}
      <section className="border-b border-mono-800">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <FadeIn delay={0} duration={500}>
                <p className="text-[10px] text-mono-500 tracking-[0.4em] mb-4">APPLICATIONS</p>
              </FadeIn>
              <FadeIn delay={100} duration={500}>
                <h2 className="text-2xl font-bold text-mono-100 tracking-wide mb-6">
                  IDEAL USE CASES
                </h2>
              </FadeIn>
              <FadeIn delay={200} duration={500}>
                <p className="text-sm text-mono-400 leading-relaxed">
                  {product.description.split('.')[0]}.
                </p>
              </FadeIn>
            </div>
            <FadeIn delay={300} duration={600}>
              <div className="space-y-4">
                {product.useCases.map((useCase, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-4 p-4 border ${colors.border} rounded-lg bg-gradient-to-r ${colors.gradient}`}
                  >
                    <span className={`text-lg font-bold ${colors.text}`}>{index + 1}</span>
                    <p className="text-sm text-mono-300">{useCase}</p>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Pricing — hidden for Polygon, Phreatic, and Data Vault */}
      {!['polygon', 'phreatic', 'datavault'].includes(product.slug) && (
      <section className="border-b border-mono-800" id="pricing">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <FadeIn delay={0} duration={500}>
            <p className="text-[10px] text-mono-500 tracking-[0.4em] mb-4 text-center">PRICING</p>
          </FadeIn>
          <FadeIn delay={100} duration={500}>
            <h2 className="text-2xl font-bold text-mono-100 tracking-wide mb-4 text-center">
              ENTERPRISE SOLUTIONS
            </h2>
          </FadeIn>
          <FadeIn delay={200} duration={500}>
            <p className="text-sm text-mono-400 text-center mb-12 max-w-xl mx-auto">
              Contact our sales team for custom pricing tailored to your organization's needs.
            </p>
          </FadeIn>
          <FadeIn delay={300} duration={600}>
            <div className="max-w-2xl mx-auto">
              <div className={`border ${colors.border} rounded-lg p-8 bg-gradient-to-br ${colors.gradient}`}>
                <div className="text-center mb-8">
                  <p className="text-[10px] text-mono-500 tracking-[0.3em] mb-2">ENTERPRISE</p>
                  <p className="text-3xl font-bold text-mono-100">Custom Pricing</p>
                  <p className="text-sm text-mono-500 mt-1">Tailored to your needs</p>
                </div>
                <div className="space-y-3 mb-8">
                  {product.pricing[0]?.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-3">
                      <CheckIcon className={`h-4 w-4 ${colors.text} mt-0.5 flex-shrink-0`} />
                      <span className="text-sm text-mono-300">{feature}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/contact-sales"
                  className={`block w-full py-3 text-center text-xs tracking-widest transition-colors ${colors.button} text-white`}
                >
                  CONTACT SALES
                </Link>
              </div>
            </div>
          </FadeIn>

          {/* Employee Access - PRECEPT only */}
          {product.slug === 'precept' && (
            <FadeIn delay={400} duration={600}>
              <div className="mt-8 flex justify-center">
                <EmployeeAccess colors={colors} />
              </div>
            </FadeIn>
          )}
        </div>
      </section>
      )}

      {/* CTA */}
      <section className="border-b border-mono-800">
        <FadeIn delay={0} duration={600}>
          <div className="max-w-7xl mx-auto px-6 py-20 text-center">
            <h2 className="text-2xl font-bold text-mono-100 tracking-wide mb-4">
              READY TO GET STARTED WITH {product.name}?
            </h2>
            <p className="text-xs text-mono-400 tracking-wide mb-8">
              CONTACT OUR TEAM FOR A DEMO
            </p>
            <Link
              to="/contact-sales"
              className="inline-block px-8 py-3 border border-mono-600 text-mono-300 text-xs tracking-widest hover:border-mono-400 transition-colors"
            >
              CONTACT SALES
            </Link>
          </div>
        </FadeIn>
      </section>

      {/* Footer */}
      <footer>
        <FadeIn delay={0} duration={400}>
          <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between">
            <div>
              <span className="text-sm font-bold text-mono-400 tracking-[0.2em]">ARGOS</span>
              <span className="text-[10px] text-mono-600 tracking-wider ml-2">
                ENVIRONMENTAL AUTOMATION
              </span>
            </div>
            <div className="flex items-center gap-8">
              <Link to="/" className="text-[10px] text-mono-500 tracking-wider hover:text-mono-300">
                HOME
              </Link>
              <Link to="/login" className="text-[10px] text-mono-500 tracking-wider hover:text-mono-300">
                LOGIN
              </Link>
              <Link to="/admin" className="text-[10px] text-cyan-500/70 tracking-wider hover:text-cyan-400">
                ADMIN
              </Link>
            </div>
          </div>
        </FadeIn>
      </footer>
    </div>
  );
}
